'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { getRandomFact } from '@/app/actions/fact-actions';
import { Lightbulb, ArrowRight, RefreshCw } from 'lucide-react';

export default function FactCard() {
    const [fact, setFact] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const fetchFact = async () => {
        setLoading(true);
        try {
            const data = await getRandomFact();
            setFact(data);
        } catch (error) {
            console.error('Failed to fetch fact:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFact();
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col h-full bg-[var(--bg-secondary)] border border-[var(--color-border)] rounded-2xl p-6 animate-pulse">
                <div className="h-6 w-24 bg-[var(--color-border)] rounded mb-4"></div>
                <div className="h-8 w-3/4 bg-[var(--color-border)] rounded mb-4"></div>
                <div className="h-20 w-full bg-[var(--color-border)] rounded mb-auto"></div>
                <div className="h-10 w-full bg-[var(--color-border)] rounded mt-4"></div>
            </div>
        );
    }

    if (!fact) return null;

    return (
        <div className="group flex flex-col h-full bg-gradient-to-br from-[var(--bg-elevated)] to-[var(--bg-secondary)] border border-amber-500/30 rounded-2xl overflow-hidden hover:border-amber-500 transition-all duration-300 shadow-sm hover:shadow-xl hover:shadow-amber-500/10">
            <div className="p-6 flex flex-col h-full relative">
                <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Lightbulb size={80} />
                </div>

                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-500 mb-4 z-10">
                    <Lightbulb size={16} />
                    <span>Did You Know?</span>
                </div>

                <h3 className="text-xl font-bold mb-4 z-10">
                    <Link href={`/interesting-facts/${fact.slug}`} className="hover:text-amber-500 transition-colors">
                        {fact.headline}
                    </Link>
                </h3>

                <div className="text-[var(--text-secondary)] text-sm line-clamp-4 mb-6 z-10 flex-grow">
                    {fact.text || fact.content || fact.excerpt || 'Click to read more about this interesting automotive fact.'}
                </div>

                <div className="mt-auto pt-4 border-t border-[var(--color-border)] flex items-center justify-between z-10">
                    <Link
                        href={`/interesting-facts/${fact.slug}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-[var(--text-primary)] hover:text-amber-500 transition-colors"
                    >
                        Read Fact <ArrowRight size={16} />
                    </Link>

                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            fetchFact();
                        }}
                        className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors rounded-full hover:bg-[var(--bg-secondary)]"
                        title="Load another fact"
                    >
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}
